import {
  applySavedSegmentHighlights,
  clearSavedSegmentHighlights,
  findTranslatedOverlayRanges,
} from '@src/utility/transcriptQuoteUtils';
import { useEffect, useMemo, useRef } from 'react';

import type {
  HighlightRange,
  TranslatedPhraseSourceInfo,
} from '@src/utility/transcriptQuoteUtils';
import type { RefObject } from 'react';

/** Stable identity so the memo below can return "nothing to wash" for free. */
const NO_RANGES: HighlightRange[] = [];

interface SavedSegmentWashOptions {
  /** Bounds this instance's word spans — never a global `document` lookup. */
  containerRef: RefObject<HTMLElement>;
  /** Source-index ranges to wash, already filtered by the header controls. */
  ranges: HighlightRange[];
  /** Range id (highlight group key) → the group's persisted color. */
  colorByKey: Record<string, string>;
  /** `data-index` (original view) or `data-initial-index` (translated). */
  spanAttr: string;
  /** Phrase projection for the translated views; undefined in the original. */
  translatedPhrases?: ReadonlyArray<TranslatedPhraseSourceInfo>;
  /**
   * Show-only / topic filter set. Read by MEMBERSHIP, not identity — see the
   * latch comment below; it is not a repaint trigger.
   */
  visibleParagraphIds?: ReadonlySet<string>;
  /**
   * The original-language redline view (`_TranscriptionDiff`) renders one span
   * per run of same-state words and stamps `data-index` only on block starts,
   * so a per-passage wash cannot be placed there: a range covering a block's
   * start index would wash the entire run, and a range starting mid-block
   * would wash nothing. Skip the pass — the same reason that view draws its own
   * overlay underlines instead of taking this imperative pass. The TRANSLATED
   * redline (`TranscriptionDiffTranslated`) is per-phrase like the non-redline
   * translated view, so it is NOT a block-span view and does get washed.
   */
  blockSpanView?: boolean;
}

/**
 * Paint the saved-segment (highlight) wash onto a transcript's word spans.
 * Extracted from `Transcript.tsx` per CLAUDE.md → "Architecture Guardrails"
 * rule 5; the effect it replaces carried two bugs (PLAT-11192).
 *
 * The wash is imperative: `applySavedSegmentHighlights` adds a class plus an
 * inline color to each in-range span, and the spans themselves render
 * `className=""` (see `TranscriptionText`'s `Element`). So the wash survives a
 * re-render but NOT a remount — and nothing about a remount changes the
 * highlight set, so no dependency array can describe when to repaint. The
 * remount causes are open-ended: a show-only toggle, an inline replacement, a
 * speaker reassignment or a background refetch all re-render the spans while
 * `ranges` keeps its identity (the range memo upstream is keyed on the word
 * COUNT, which none of those edits change).
 *
 * So this effect runs after EVERY commit and decides for itself whether there
 * is work to do, latching on the inputs plus the first span it washed: a
 * remount leaves that node disconnected, which is two property reads to
 * detect. Depending on the word array instead would look narrower but is
 * strictly worse — that array is a fresh identity on every render (CLAUDE.md
 * pitfall #18), so it would re-run a full clear plus one attribute-selector
 * scan per highlighted word on every render, playback ticks included.
 */
export function useSavedSegmentWash({
  containerRef,
  ranges,
  colorByKey,
  spanAttr,
  translatedPhrases,
  visibleParagraphIds,
  blockSpanView = false,
}: SavedSegmentWashOptions): void {
  // The translated views stamp `data-initial-index` on PHRASE LEADS, not on
  // every source word, so source ranges are re-anchored into lead space before
  // painting — the same projection the overlay underline uses. A translation
  // doesn't split on the source's word boundaries, so the wash covers the
  // phrases the passage maps onto rather than its exact words.
  //
  // Note this hook feeds that projection something overlays never did: a
  // highlight GROUP's ranges all share one id, one per passage. Collapsing by
  // id merged them into a single span reaching from the group's first passage
  // to its last — a four-passage group washed 28% of a transcript. The
  // projection now collapses per source range (PLAT-11192). Two passages over
  // OVERLAPPING source text can still wash slightly wide (see
  // `findTranslatedOverlayRanges` on its disjoint-ranges invariant); that shape
  // needs two selections covering the same words, and the wider wash is the
  // accepted outcome there.
  const painted = useMemo(() => {
    if (blockSpanView) return NO_RANGES;
    if (!translatedPhrases) return ranges;
    if (ranges.length === 0 || translatedPhrases.length === 0) return NO_RANGES;
    return findTranslatedOverlayRanges(ranges, translatedPhrases);
  }, [ranges, translatedPhrases, blockSpanView]);

  const lastPaintRef = useRef<{
    key: string;
    sentinel: HTMLElement | null;
  } | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    // The filter set enters the latch by MEMBERSHIP, not identity and not
    // size. Identity is out because it is a fresh Set on every render while
    // show-only is active, so comparing it would repaint every commit in
    // exactly the mode that plays only the highlights. Size is out because two
    // different filters of equal size are indistinguishable by it, and a swap
    // between them can both reveal a passage the previous filter hid (leaving
    // the latch stuck on `sentinel: null`, with nothing to detect) and reveal a
    // second passage while the sentinel's own span stays mounted. Sorted so a
    // set rebuilt in another order doesn't read as a change. Measured at
    // 0.6-6 µs for the 10-120 paragraph ids a transcript actually produces;
    // it only turns costly in the thousands, which a paragraph set never is.
    const key = [
      spanAttr,
      visibleParagraphIds
        ? JSON.stringify([...visibleParagraphIds].sort())
        : '*',
      JSON.stringify(painted),
      JSON.stringify(colorByKey),
    ].join('|');
    const last = lastPaintRef.current;
    // `?? true` treats "found nothing to wash" as up to date: re-running would
    // find nothing again, and the filter term above covers the case that
    // changes that answer.
    if (last && last.key === key && (last.sentinel?.isConnected ?? true)) {
      return;
    }
    clearSavedSegmentHighlights(container);
    const sentinel =
      painted.length > 0
        ? applySavedSegmentHighlights(container, painted, colorByKey, spanAttr)
        : null;
    lastPaintRef.current = { key, sentinel };
    // No cleanup and no dependency array. The clear above is the only one
    // needed — a cleanup would clear on every commit and undo the paint, and
    // at unmount the container is this Transcript's own root element, so its
    // spans are discarded with it.
  });
}
